/**
 * StatTile - Number-and-label primitive used everywhere a single
 * metric needs to display: dashboard counts, summary rows, etc.
 *
 * Single responsibility: render `value` (large) above `label`
 * (small, muted) with optional click handling. Carries no domain
 * knowledge — everything domain-specific (deltas, formatting,
 * trend arrows) belongs to the molecule that wraps it.
 *
 * @example
 * <StatTile value={42} label="Open Work Orders" />
 * <StatTile value="$12,345" label="Revenue this month" />
 */
import React from 'react';

/* ============================================
 * TYPE DEFINITIONS
 * ============================================ */

export interface StatTileProps {
    /** The metric value. Numbers render as-is; strings can include
     *  pre-formatted currency, percentages, etc. */
    value: number | string;
    /** Short descriptor below the value */
    label: string;
    /** Optional click handler — when present, the tile renders as a
     *  button with hover affordance. */
    onClick?: () => void;
    /** Visual scale; defaults to 'md'. */
    size?: 'sm' | 'md' | 'lg';
    /** Optional extra classes for the outer wrapper */
    className?: string;
}

/* ============================================
 * STYLE LOOKUP
 * ============================================ */

const VALUE_SIZE: Record<NonNullable<StatTileProps['size']>, string> = {
    sm: 'text-20px',
    md: 'text-22px tablet:text-28px',
    lg: 'text-36px',
};

const LABEL_SIZE: Record<NonNullable<StatTileProps['size']>, string> = {
    sm: 'text-11px',
    md: 'text-12px',
    lg: 'text-13px',
};

/* ============================================
 * COMPONENT
 * ============================================ */

export const StatTile: React.FC<StatTileProps> = ({
    value,
    label,
    onClick,
    size = 'md',
    className,
}) => {
    /* Render as a button when interactive, otherwise as a div. */
    const Wrapper: React.ElementType = onClick ? 'button' : 'div';
    const wrapperProps = onClick ? { type: 'button' as const, onClick } : {};

    return (
        <Wrapper
            {...wrapperProps}
            data-ref="atom-stat-tile"
            className={[
                'stat-tile',
                'inline-flex flex-col items-start',
                onClick ? 'cursor-pointer hover:opacity-80 transition-opacity' : '',
                className ?? '',
            ].join(' ')}
        >
            <span className={`stat-tile__value ${VALUE_SIZE[size]} font-sans font-semibold text-brand-text leading-tight min-w-0 break-words max-w-full tabular-nums`}>
                {value}
            </span>
            <span className={`stat-tile__label ${LABEL_SIZE[size]} font-sans text-brand-text-muted uppercase tracking-wide mt-2px`}>
                {label}
            </span>
        </Wrapper>
    );
};

export default StatTile;
